import React from 'react';
import { Loading, Title, useTranslate } from 'react-admin';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import useReviewGrid, { axesByBlock } from './useReviewGrid';

// The explanations of the grid: what each level means on each axis, and what each stage expects.
const ReviewGridPage = () => {
    const translate = useTranslate();
    const grid = useReviewGrid();

    if (!grid) {
        return <Loading />;
    }

    return (
        <Card sx={{ marginTop: '16px' }}>
            <Title title={translate('Review grid')} />
            <CardContent>
                <Typography variant="h5" gutterBottom>{translate('Review grid')}</Typography>
                <Typography sx={{ marginBottom: '16px' }}>{grid.intro}</Typography>

                <Typography variant="h6">{translate('The four levels')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, margin: '8px 0 24px' }}>
                    {grid.levels.map(level => (
                        <Card key={level.value} variant="outlined">
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{level.value} · {level.label}</Typography>
                                <Typography variant="body2" color="text.secondary">{level.description}</Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                <Typography variant="h6">{translate('How to score')}</Typography>
                <ul>
                    {grid.rules.map((rule, index) => <li key={index}><Typography>{rule}</Typography></li>)}
                </ul>

                <Typography variant="h6">{translate('Target by stage')}</Typography>
                <Table size="small" sx={{ marginBottom: '24px', maxWidth: 900 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell />
                            {grid.blocks.map(block => <TableCell key={block.key}>{block.label}</TableCell>)}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {grid.stages.map(stage => (
                            <TableRow key={stage.key}>
                                <TableCell sx={{ fontWeight: 600 }}>{stage.label}</TableCell>
                                {grid.blocks.map(block => (
                                    <TableCell key={block.key}>{grid.targets[stage.key]?.[block.key] ?? translate('not expected')}</TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {axesByBlock(grid).map(block => (
                    <Box key={block.key} sx={{ marginBottom: '24px' }}>
                        <Typography variant="h6">{block.label}</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '20%' }}>{translate('Axis')}</TableCell>
                                    {grid.levels.map(level => (
                                        <TableCell key={level.value} sx={{ width: '20%' }}>{level.value} · {level.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {block.axes.map(axis => (
                                    <TableRow key={axis.key} sx={{ verticalAlign: 'top' }}>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 600 }}>{axis.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">{axis.question}</Typography>
                                        </TableCell>
                                        {axis.levels.map((description, index) => (
                                            <TableCell key={index}><Typography variant="body2">{description}</Typography></TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ))}
            </CardContent>
        </Card>
    );
};

export default ReviewGridPage;
